/**
 * Widget Preview Component
 * =========================
 * Live preview of widget with current configuration
 *
 * Added: 2025-11-18 - Phase 4 Feature 1
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Smartphone,
  Computer,
  Refresh,
  ChatBubble,
  Close,
  Send,
} from '@mui/icons-material';

interface WidgetPreviewProps {
  config: {
    widget_color?: string;
    position?: 'left' | 'right';
    launcher_title?: string;
    welcome_heading?: string;
    welcome_tagline?: string;
    reply_time?: string;
    widget_style?: 'standard' | 'expanded' | 'compact';
    dark_mode?: 'light' | 'dark' | 'auto';
  };
}

interface MockMessage {
  id: number;
  content: string;
  type: 'incoming' | 'outgoing';
  timestamp: string;
  sender?: {
    name: string;
    avatar?: string;
  };
}

export const WidgetPreview: React.FC<WidgetPreviewProps> = ({ config }) => {
  const [viewMode, setViewMode] = useState<'mobile' | 'desktop'>('desktop');
  const [isOpen, setIsOpen] = useState(false);
  const [mockMessages, setMockMessages] = useState<MockMessage[]>([
    {
      id: 1,
      content: config.welcome_heading || 'Hi there! How can we help you today?',
      type: 'incoming',
      timestamp: new Date(Date.now() - 60000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      sender: {
        name: 'Support Agent',
        avatar: '👤',
      },
    },
    {
      id: 2,
      content: 'Hello! I need help with my account.',
      type: 'outgoing',
      timestamp: new Date(Date.now() - 30000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    },
    {
      id: 3,
      content: 'Of course! I\'d be happy to help you with your account. What specific issue are you experiencing?',
      type: 'incoming',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      sender: {
        name: 'Support Agent',
        avatar: '👤',
      },
    },
  ]);

  // Update welcome message when config changes
  useEffect(() => {
    setMockMessages(prev => {
      const updated = [...prev];
      if (updated[0]) {
        updated[0] = {
          ...updated[0],
          content: config.welcome_heading || 'Hi there! How can we help you today?',
        };
      }
      return updated;
    });
  }, [config.welcome_heading]);

  const widgetColor = config.widget_color || '#1f93ff';
  const position = config.position || 'right';
  const launcherTitle = config.launcher_title || 'Chat with us';
  const replyTime = config.reply_time || 'Typically replies in a few hours';
  const isDark = config.dark_mode === 'dark'; // For preview, respect dark mode setting

  const getPreviewContainerWidth = () => {
    return viewMode === 'mobile' ? '375px' : '100%';
  };

  const handleRefresh = () => {
    setIsOpen(false);
    setTimeout(() => {
      setMockMessages([
        {
          id: Date.now(),
          content: config.welcome_heading || 'Hi there! How can we help you today?',
          type: 'incoming',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          sender: {
            name: 'Support Agent',
            avatar: '👤',
          },
        },
      ]);
    }, 300);
  };

  return (
    <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Live Preview</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, value) => value && setViewMode(value)}
            size="small"
          >
            <ToggleButton value="desktop">
              <Tooltip title="Desktop View">
                <Computer fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="mobile">
              <Tooltip title="Mobile View">
                <Smartphone fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh Preview">
            <IconButton size="small" onClick={handleRefresh}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Preview Container */}
      <Box
        sx={{
          width: getPreviewContainerWidth(),
          height: '600px',
          mx: 'auto',
          position: 'relative',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: viewMode === 'mobile' ? '36px' : '8px',
          padding: viewMode === 'mobile' ? '20px' : '40px',
          boxShadow: viewMode === 'mobile' ? '0 20px 60px rgba(0,0,0,0.3)' : 'none',
          overflow: 'hidden',
        }}
      >
        {/* Mobile Frame Notch */}
        {viewMode === 'mobile' && (
          <Box
            sx={{
              position: 'absolute',
              top: '0',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '150px',
              height: '25px',
              background: '#000',
              borderBottomLeftRadius: '20px',
              borderBottomRightRadius: '20px',
              zIndex: 10,
            }}
          />
        )}

        {/* Widget Container */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            background: isDark ? '#1a1a1a' : '#ffffff',
            borderRadius: viewMode === 'mobile' ? '20px' : '0',
            overflow: 'hidden',
          }}
        >
          {/* Chat Window (when open) */}
          {isOpen && (
            <Box
              sx={{
                position: 'absolute',
                bottom: viewMode === 'mobile' ? '0' : '90px',
                right: viewMode === 'mobile' ? '0' : position === 'right' ? '20px' : 'auto',
                left: viewMode === 'mobile' ? '0' : position === 'left' ? '20px' : 'auto',
                width: viewMode === 'mobile' ? '100%' : '380px',
                height: viewMode === 'mobile' ? '100%' : '600px',
                background: '#fff',
                borderRadius: viewMode === 'mobile' ? '0' : '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                zIndex: 1000,
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  background: widgetColor,
                  color: '#fff',
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                    }}
                  >
                    👤
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Support Team
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {replyTime}
                    </Typography>
                  </Box>
                </Box>
                <IconButton size="small" onClick={() => setIsOpen(false)} sx={{ color: '#fff' }}>
                  <Close />
                </IconButton>
              </Box>

              {/* Messages */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2, background: '#f5f7fa' }}>
                {mockMessages.map((message) => (
                  <Box
                    key={message.id}
                    sx={{
                      mb: 2,
                      display: 'flex',
                      justifyContent: message.type === 'outgoing' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <Box sx={{ maxWidth: '75%' }}>
                      {message.type === 'incoming' && message.sender && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {message.sender.name}
                          </Typography>
                        </Box>
                      )}
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: '12px',
                          background: message.type === 'outgoing' ? widgetColor : '#fff',
                          color: message.type === 'outgoing' ? '#fff' : '#000',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                        }}
                      >
                        <Typography variant="body2">{message.content}</Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: 'block', fontSize: '11px' }}
                      >
                        {message.timestamp}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Input */}
              <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', background: '#fff' }}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    p: 1,
                    background: '#f5f7fa',
                    borderRadius: '24px',
                    alignItems: 'center',
                  }}
                >
                  <input
                    type="text"
                    placeholder="Type your message..."
                    disabled
                    style={{
                      flex: 1,
                      border: 'none',
                      background: 'transparent',
                      outline: 'none',
                      padding: '8px 12px',
                      fontSize: '14px',
                    }}
                  />
                  <IconButton size="small" disabled sx={{ color: widgetColor }}>
                    <Send fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                  Powered by Converse.ai
                </Typography>
              </Box>
            </Box>
          )}

          {/* Launcher Button */}
          <Box
            sx={{
              position: 'absolute',
              bottom: viewMode === 'mobile' ? '20px' : '20px',
              right: position === 'right' ? '20px' : 'auto',
              left: position === 'left' ? '20px' : 'auto',
              zIndex: 999,
            }}
          >
            <Tooltip title={launcherTitle} placement={position === 'right' ? 'left' : 'right'}>
              <Box
                onClick={() => setIsOpen(!isOpen)}
                sx={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: widgetColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                  },
                }}
              >
                {isOpen ? (
                  <Close sx={{ color: '#fff', fontSize: '28px' }} />
                ) : (
                  <ChatBubble sx={{ color: '#fff', fontSize: '28px' }} />
                )}
              </Box>
            </Tooltip>

            {/* Unread Badge (Demo) */}
            {!isOpen && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '-5px',
                  right: '-5px',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: '#ef4444',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 600,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}
              >
                1
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
        This is a live preview. Changes to settings will update in real-time.
      </Typography>
    </Paper>
  );
};

export default WidgetPreview;
